<template>
  <div>
    <pdf-preview
      v-show="inPreview"
      :inPreview="inPreview"
      :generatingPreview="generatingPreview"
      :saving="saving"
      :pdfUrl="pdfUrl"
      :backToForm="backToForm"
      :save="save"
    />
    <div v-show="!inPreview">
      <form data-vv-scope="custom-document" autocomplete="off" @submit.prevent>
      <h2 class="mb-10">Memorándum</h2>

      <!-- Sección: Remitente -->
      <div class="row">
        <div class="col-12">
          <h4 class="mt-4 mb-3">Remitente</h4>
          <vs-divider />
        </div>
      </div>

      <div class="row form-group">
        <label for="sender" class="col-3 col-form-label">
          De <span class="text-danger">*</span>
        </label>
        <div class="col-9">
          <list-user
            autocomplete
            class="w-100"
            data-cy="sender"
            data-vv-as="DE"
            name="sender"
            label=""
            placeholder="Selecciona un usuario"
            v-model="formData.sender"
            :danger="errors.has('custom-document.sender')"
            :danger-text="errors.first('custom-document.sender')"
            v-validate="'required'"
            @full-user="addSenderData"
            ref="sender"
          />
        </div>
      </div>

      <div class="row form-group">
        <label for="senderPosition" class="col-3 col-form-label">
          Cargo <span class="text-danger">*</span>
        </label>
        <div class="col-9">
          <b-form-input
            data-cy="senderPosition"
            data-vv-as="cargo"
            name="senderPosition"
            type="text"
            placeholder="Cargo del remitente"
            v-model="formData.senderPosition"
            :state="errors.has('custom-document.senderPosition') ? false : null"
            v-validate="'required'"
          />
          <div
            v-if="errors.has('custom-document.senderPosition')"
            class="con-text-validation span-text-validation-danger v-enter-to"
          >
            <span class="span-text-validation">
              {{ errors.first('custom-document.senderPosition') }}
            </span>
          </div>
        </div>
      </div>

      <div class="row form-group">
        <label for="senderCompany" class="col-3 col-form-label">
          Empresa <span class="text-danger">*</span>
        </label>
        <div class="col-9">
          <b-form-input
            data-cy="senderCompany"
            data-vv-as="empresa"
            name="senderCompany"
            type="text"
            v-model="formData.senderCompany"
            :state="errors.has('custom-document.senderCompany') ? false : null"
            v-validate="'required'"
            disabled
          />
          <div
            v-if="errors.has('custom-document.senderCompany')"
            class="con-text-validation span-text-validation-danger v-enter-to"
          >
            <span class="span-text-validation">
              {{ errors.first('custom-document.senderCompany') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Sección: Destinatario -->
      <div class="row">
        <div class="col-12">
          <h4 class="mt-4 mb-3">Destinatario</h4>
          <vs-divider />
        </div>
      </div>

      <div class="row form-group">
        <label for="receiver" class="col-3 col-form-label">A</label>
        <div class="col-9">
          <list-user
            autocomplete
            class="w-100"
            data-cy="receiver"
            data-vv-as="A"
            name="receiver"
            label=""
            placeholder="Selecciona un usuario (opcional)"
            v-model="formData.receiver"
            :danger="errors.has('custom-document.receiver')"
            :danger-text="errors.first('custom-document.receiver')"
            @full-user="addReceiverData"
            ref="receiver"
          />
        </div>
      </div>

      <div class="row form-group">
        <label for="receiverPosition" class="col-3 col-form-label">Cargo</label>
        <div class="col-9">
          <b-form-input
            data-cy="receiverPosition"
            data-vv-as="cargo"
            name="receiverPosition"
            type="text"
            placeholder="Cargo del destinatario (opcional)"
            v-model="formData.receiverPosition"
          />
        </div>
      </div>

      <div class="row form-group">
        <label for="receiverCompany" class="col-3 col-form-label">Empresa</label>
        <div class="col-9">
          <b-form-input
            data-cy="receiverCompany"
            data-vv-as="empresa"
            name="receiverCompany"
            type="text"
            placeholder="Empresa del destinatario (opcional)"
            v-model="formData.receiverCompany"
            disabled
          />
        </div>
      </div>

      <!-- Sección: Contenido del Memorándum -->
      <div class="row">
        <div class="col-12">
          <h4 class="mt-4 mb-3">Contenido del Memorándum</h4>
          <vs-divider />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <b-form-group>
            <label>ANT</label>
            <editor-text
              data-cy="background"
              name="background"
              data-vv-as="ant"
              :max-length="500"
              ref="background"
              :toolbar-items="{
                items: [
                  'undo', 'redo',
                  '|', 'heading',
                  '|', 'bold', 'italic',
                  '|', 'insertTable',
                  '|', 'numberedList', 'bulletedList'
                ]
              }"
              v-model="formData.background"
              with-counter
            />
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <b-form-group>
            <label>Materia <span class="text-danger">*</span></label>
            <editor-text
              :danger="errors.has('custom-document.matter')"
              :danger-text="errors.first('custom-document.matter')"
              data-cy="matter"
              name="matter"
              data-vv-as="mat"
              :max-length="500"
              ref="matter"
              :toolbar-items="{
                items: [
                  'undo', 'redo',
                  '|', 'heading',
                  '|', 'bold', 'italic',
                  '|', 'insertTable',
                  '|', 'numberedList', 'bulletedList'
                ]
              }"
              v-validate="'required'"
              v-model="formData.matter"
              with-counter
            />
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <b-form-group>
            <label>Cuerpo del texto <span class="text-danger">*</span></label>
            <editor-text
              :danger="errors.has('custom-document.description')"
              :danger-text="errors.first('custom-document.description')"
              data-cy="description"
              data-vv-as="cuerpo del texto"
              :max-length="maxLengthBodyText"
              name="description"
              ref="description"
              :toolbar-items="{
                items: [
                  'undo', 'redo',
                  '|', 'heading',
                  '|', 'bold', 'italic',
                  '|', 'insertTable',
                  '|', 'numberedList', 'bulletedList'
                ]
              }"
              v-validate="'required'"
              v-model="formData.description"
              with-counter
            />
          </b-form-group>
        </div>
      </div>

      <!-- Sección: Visación y Distribución -->
      <div class="row">
        <div class="col-12">
          <h4 class="mt-4 mb-3">Visación y Distribución</h4>
          <vs-divider />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group>
            <label>Visadores del proceso <span class="text-danger">*</span></label>
            <list-user
              is-multiple
              autocomplete
              class="w-100"
              data-cy="visators"
              data-vv-as="visadores"
              name="visators"
              label=""
              placeholder="Selecciona los visadores"
              :danger="visatorsError !== ''"
              :danger-text="visatorsError"
              @full-user="setInitials"
              ref="visators"
            />
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <b-form-group>
            <label>Distribución interna - Direcciones</label>
            <v-select
              v-model="formData.directions"
              :options="directions"
              name="directions"
              label="label"
              data-cy="directions"
              placeholder="Selecciona la/s dirección/es (opcional)"
              multiple
              @input="searchUnits"
            >
              <template slot="no-options">
                No hay direcciones
              </template>
            </v-select>
            <b-form-checkbox
              v-model="selectAllDirections"
              @change="selectDirections"
              :disabled="!directions.length"
              class="mt-2"
            >
              Seleccionar todas
            </b-form-checkbox>
          </b-form-group>
        </div>
        <div class="col-md-6" v-if="formData.directions.length">
          <b-form-group>
            <label>Distribución interna - Unidades</label>
            <v-select
              v-model="formData.units"
              :options="units"
              name="units"
              label="label"
              data-cy="units"
              placeholder="Selecciona la/s unidad/es (opcional)"
              multiple
            >
              <template slot="no-options">
                No hay unidades
              </template>
            </v-select>
            <b-form-checkbox
              v-model="selectAllUnits"
              @change="selectUnits"
              :disabled="!units.length"
              class="mt-2"
            >
              Seleccionar todas
            </b-form-checkbox>
          </b-form-group>
        </div>
      </div>

      <!-- Sección: Adjuntos -->
      <div class="row">
        <div class="col-12">
          <h4 class="mt-4 mb-3">Adjuntos</h4>
          <vs-divider />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="border rounded p-3">
            <div class="row">
              <div class="col-md-3">
                <UploadFile
                  permission="*"
                  accept="application/pdf, .doc, .docx, .xlsx, .ppt"
                  text="Seleccionar documentos"
                  v-model="fileOthers"
                  upload-to="ed"
                  :button-class="{ 'btn-block': true }"
                  container-class="w-100"
                  @input="addOthersFile()"
                />
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-3">
                <div class="col-md-6 px-0">
                  <b-button
                    variant="secondary"
                    class="btn-block btn-sm"
                    data-cy="from-repository"
                    v-has-permission="'ED060'"
                    v-b-modal="'add-file-repository'"
                  >
                    <feather-icon icon="ServerIcon" class="mr-1" size="14"/>
                    Desde repositorio
                  </b-button>
                </div>
              </div>
            </div>

            <div v-if="formData.attachments.length" class="row mt-3">
              <div class="col-12">
                <label class="mb-2">Archivos adjuntos:</label>
              </div>
              <div class="col-2" v-for="file in formData.attachments" :key="file.id">
                <div class="d-flex flex-column align-items-center border rounded p-2">
                  <vs-icon icon="description" size="25px" color="primary" />
                  <b class="text-truncate w-100 text-center mt-1" :title="file.name">{{ file.name }}</b>
                  <vs-button
                    color="danger"
                    type="flat"
                    icon="clear"
                    size="small"
                    class="mt-1"
                    @click="removeOthersFile(file.id)"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="!!error.messageOthers"
              class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to text-left mt-2"
            >
              <span class="span-text-validation">
                {{ error.messageOthers }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="row justify-end mt-5">
        <vs-button
          class="mr-3"
          data-cy="cancel"
          @click="back()"
          color="primary"
          :disabled="saving"
        >
          Cancelar
        </vs-button>
        <vs-button
          class="mr-3"
          data-cy="preview"
          @click="preview"
          color="warning"
          :disabled="saving"
        >
          Previsualizar
        </vs-button>
        <vs-button
          class="mr-5"
          data-cy="save"
          @click="save()"
          color="success"
          :disabled="saving"
        >
          Crear Memorándum
          <span
            v-show="saving"
            class="spinner-border spinner-border-sm text-light ml-2"
          />
        </vs-button>
      </div>
      <repository-selector
        modal-id="add-file-repository"
        :add-file="addFileRepo"
        :files="formData.attachments"
        :remove-file="removeOthersFile"
      />
    </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { ioCreateCustomDocument, ioGetCustomDocument } from '@/febos/servicios/api/customDocuments.api';
import { ioCompanyGroups } from '@/febos/servicios/api/empresas.api';
import EditorText from '@/febos/global/customDocuments/vistas/empresas/72235100-2/components/EditorText';
import ListUser from '@/febos/chile/lists/components/ListUser';
import PdfPreview from '@/febos/global/customDocuments/vistas/empresas/61980520-8/components/PdfPreview.vue';
import UploadFile from '@/febos/global/_vue/componentes/UploadFile';
import RepositorySelector from '@/febos/global/customDocuments/components/repository-selector/RepositorySelector.vue';

export default {
  name: 'MEMO',
  components: {
    EditorText,
    ListUser,
    PdfPreview,
    UploadFile,
    RepositorySelector
  },
  data() {
    return {
      formData: {
        sender: '',
        senderPosition: '',
        senderCompany: 'Municipalidad de Machalí',
        receiver: '',
        receiverPosition: '',
        receiverCompany: 'Municipalidad de Machalí',
        background: '',
        matter: '',
        description: '',
        visators: [],
        visatorsInitials: '',
        directions: [],
        units: [],
        attachments: [],
        date: (() => {
          const d = new Date();
          const day = String(d.getDate()).padStart(2, '0');
          const months = [
            'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
            'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
          ];
          const month = months[d.getMonth()];
          const monthNumber = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          return {
            day,
            month,
            monthNumber,
            year
          };
        })()
      },
      directions: [],
      units: [],
      maxLengthBodyText: 100000,
      saving: false,
      visatorsError: '',
      selectedVisators: [],
      inPreview: false,
      generatingPreview: false,
      pdfUrl: '',
      selectAllDirections: false,
      selectAllUnits: false,
      fileOthers: {},
      error: {
        messageOthers: ''
      }
    };
  },
  computed: {
    ...mapGetters('List', ['groupsState']),
    ...mapGetters('Empresas', ['empresa']),
    ...mapGetters('Usuario', ['currentUser'])
  },
  methods: {
    ...mapActions('List', ['fetchGroups']),
    addSenderData(user) {
      this.formData.senderPosition = user.position;
    },
    addReceiverData(user) {
      this.formData.receiverPosition = user.position;
    },
    setInitials(users) {
      if (!users || !users.length) {
        this.formData.visatorsInitials = '';
        this.formData.visators = [];
        this.selectedVisators = [];
        return;
      }
      this.selectedVisators = users;
      this.formData.visators = users;
      this.visatorsError = '';
      this.formData.visatorsInitials = users
        .map((u) => u.label.split(' ').map((word) => word.charAt(0).toUpperCase()).join(''))
        .join('/ ');
    },
    async searchUnits(directions) {
      const directionsId = directions.map((dir) => dir.id).join(',');
      const { data } = await ioCompanyGroups({
        empresaId: this.empresa.id,
        filtros: `padreId:${ directionsId}`
      });
      this.units = data.grupos.map((group) => ({
        ...group,
        id: group.id,
        label: group.nombre,
        value: group.id
      }));
    },
    selectDirections(selectAll) {
      if (selectAll) {
        this.searchUnits(this.directions);
        this.formData.directions = [...this.directions];
      } else {
        this.formData.directions = [];
        this.formData.units = [];
      }
    },
    selectUnits(selectAll) {
      if (selectAll) {
        this.formData.units = [...this.units];
      } else {
        this.formData.units = [];
      }
    },
    async addFileRepo(file) {
      if (!Object.keys(file).length) {
        return;
      }

      if (file.failed) {
        this.error.message = 'La carga del archivo falló, inténtalo de nuevo';
      }

      this.formData.attachments.push(file);
      this.error.message = '';
    },
    async addOthersFile() {
      if (!Object.keys(this.fileOthers).length) {
        return;
      }

      if (this.fileOthers.failed) {
        this.error.messageOthers = 'La carga del archivo falló, inténtalo de nuevo';
        return;
      }

      this.formData.attachments.push(this.fileOthers);
      this.error.messageOthers = '';
    },
    removeOthersFile(fileId) {
      this.formData.attachments = this.formData.attachments
        .filter((file) => file.id !== fileId);
    },
    async validateForm(scope) {
      const result = await this.$validator.validateAll(scope);
      return !!result;
    },
    backToForm() {
      this.inPreview = false;
    },
    async preview() {
      const isValid = await this.validateForm('custom-document');

      if (!this.selectedVisators.length) {
        this.visatorsError = 'El campo visadores es obligatorio';
      }

      if (!isValid || !this.selectedVisators.length) {
        return;
      }

      this.pdfUrl = '';
      this.inPreview = true;
      this.generatingPreview = true;

      const textPlainBodyText = this.$refs.description.getTextPlain();
      const textPlainBackground = this.$refs.background.getTextPlain();
      const textPlainMatter = this.$refs.matter.getTextPlain();

      if (textPlainBodyText.length > this.maxLengthBodyText) {
        this.$bvToast.toast(
          'El campo cuerpo del texto tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        this.generatingPreview = false;
        this.inPreview = false;
        return;
      }

      if (textPlainBackground.length > 500) {
        this.$bvToast.toast(
          'El campo ANT tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        this.generatingPreview = false;
        this.inPreview = false;
        return;
      }

      if (textPlainMatter.length > 500) {
        this.$bvToast.toast(
          'El campo Materia tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        this.generatingPreview = false;
        this.inPreview = false;
        return;
      }

      try {
        this.$bvToast.toast(
          'Generando previsualización',
          {
            title: 'En camino...',
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 3000
          }
        );

        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            simular: 'si',
          },
          formData: {
            sender: this.$refs.sender.getOption(),
            senderPosition: this.formData.senderPosition,
            senderCompany: this.formData.senderCompany,
            receiver: this.$refs.receiver ? this.$refs.receiver.getOption() : null,
            receiverPosition: this.formData.receiverPosition,
            receiverCompany: this.formData.receiverCompany,
            background: this.formData.background.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
            matter: this.formData.matter.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
            description: this.formData.description.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
            textPlainDescription: textPlainBodyText,
            textPlainBackground,
            textPlainMatter,
            visators: this.formData.visators,
            visatorsInitials: this.formData.visatorsInitials,
            directions: this.formData.directions.map((item, index) => ({
              ...item,
              number: index + 1
            })),
            units: this.formData.units.map((item, index) => ({
              ...item,
              number: this.formData.directions.length + index + 1
            })),
            date: this.formData.date,
            requesterInitials: this.currentUser.nombre
              .split(' ')
              .map((word) => word.charAt(0).toLowerCase())
              .join(''),
            attachments: this.formData.attachments,
            borrador: true,
          }
        };

        const { data: { pdf } } = await ioCreateCustomDocument(payload);

        if (!pdf || pdf === '') {
          this.$bvToast.toast(
            'No se pudo generar la previsualización',
            {
              title: 'Ups!',
              variant: 'warning',
              pauseOnHover: true,
              solid: true,
              autoHideDelay: 5000
            }
          );
          this.backToForm();
        }

        this.pdfUrl = `${pdf}#toolbar=0&navpanes=0&scrollbar=0`;
      } finally {
        this.generatingPreview = false;
      }
    },
    async save() {
      const isValid = await this.validateForm('custom-document');

      if (!this.selectedVisators.length) {
        this.visatorsError = 'El campo visadores es obligatorio';
      }

      if (!isValid || !this.selectedVisators.length) {
        return;
      }

      const textPlainBodyText = this.$refs.description.getTextPlain();
      const textPlainBackground = this.$refs.background.getTextPlain();
      const textPlainMatter = this.$refs.matter.getTextPlain();

      if (textPlainBodyText.length > this.maxLengthBodyText) {
        this.$bvToast.toast(
          'El campo cuerpo del texto tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        return;
      }

      if (textPlainBackground.length > 500) {
        this.$bvToast.toast(
          'El campo ANT tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        return;
      }

      if (textPlainMatter.length > 500) {
        this.$bvToast.toast(
          'El campo Materia tiene más caracteres de los permitidos',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000,
          }
        );
        return;
      }

      this.saving = true;
      this.$bvToast.toast(
        'Guardando documento',
        {
          title: 'En camino...',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 2000
        }
      );

      // Preparar el payload para enviar al backend
      const payload = {
        type: this.$route.params.type, // 'MEMO'
        query: {
          numerar: 'si',
          ...this.$route.query
        },
        formData: {
          sender: this.$refs.sender.getOption(),
          senderPosition: this.formData.senderPosition,
          senderCompany: this.formData.senderCompany,
          receiver: this.$refs.receiver ? this.$refs.receiver.getOption() : null,
          receiverPosition: this.formData.receiverPosition,
          receiverCompany: this.formData.receiverCompany,
          background: this.formData.background.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
          matter: this.formData.matter.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
          description: this.formData.description.replace(/<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, ''),
          textPlainDescription: textPlainBodyText,
          textPlainBackground,
          textPlainMatter,
          visators: this.formData.visators,
          visatorsInitials: this.formData.visatorsInitials,
          directions: this.formData.directions.map((item, index) => ({
            ...item,
            number: index + 1
          })),
          units: this.formData.units.map((item, index) => ({
            ...item,
            number: this.formData.directions.length + index + 1
          })),
          date: this.formData.date,
          requesterInitials: this.currentUser.nombre
            .split(' ')
            .map((word) => word.charAt(0).toLowerCase())
            .join(''),
          attachments: this.formData.attachments
        }
      };

      try {
        const response = await ioCreateCustomDocument(payload);
        await this.generatePdf(response.data.document.id);
      } catch (error) {
        this.$bvToast.toast(
          'Error al guardar el documento',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000
          }
        );
        this.saving = false;
      }
    },
    async generatePdf(id) {
      this.$bvToast.toast(
        'Generando archivo PDF',
        {
          title: 'En camino...',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          autoHideDelay: 5000
        }
      );

      try {
        const response = await ioGetCustomDocument('MEMO', id, { pdf: 'si' });
        this.$bvToast.toast(
          'Archivo generado',
          {
            title: 'Listo!',
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 2000
          }
        );
        window.open(response.data.pdf, '_blank');
        this.back();
      } catch (error) {
        this.$bvToast.toast(
          'Error al generar el PDF',
          {
            title: '¡Oops!',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000
          }
        );
      } finally {
        this.saving = false;
      }
    },
    back() {
      /* eslint-disable no-restricted-globals */
      history.back();
    }
  },
  async created() {
    await this.fetchGroups();
    this.directions = this.groupsState.list.filter(
      (group) => group.padreId === '' || !group.padreId
    );
  }
};
</script>
